import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PublicContentLayout } from "@/components/public/PublicContentLayout";
import { PUBLIC_ROUTES } from "@/routes/public";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getPublicCampaign,
  submitQuestionnaire,
  storePendingVoucher,
  type PublicCampaign,
  type PublicCampaignQuestion,
  type QuestionnaireResponse,
} from "@/services/api/publicCampaigns";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Gift, ArrowRight, ArrowLeft, Copy } from "lucide-react";

export default function CampaignQuestionnairePage() {
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const [_searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const locale = useRouteLocale();

  const [campaign, setCampaign] = useState<PublicCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [voucherCode, setVoucherCode] = useState<string | null>(null);
  const [thankYouMessage, setThankYouMessage] = useState<string | null>(null);
  const [ctaUrl, setCtaUrl] = useState<string | null>(null);
  const [ctaText, setCtaText] = useState<string | null>(null);

  // Responses state
  const [responses, setResponses] = useState<Record<number, string>>({});

  // Step mode state
  const [currentStep, setCurrentStep] = useState(0);

  // JUB-188: the tab names the campaign; an unknown campaign reads as a not-found page and is not indexed.
  usePageMeta({
    title: campaign ? `${campaign.name} | Jubiel` : error ? t("httpErrors.404.metaTitle") : `${t("campaign.specialOffer")} | Jubiel`,
    description: campaign?.name ?? t("campaign.specialOffer"),
    locale,
    path: window.location.pathname,
    noindex: !!error,
  });

  useEffect(() => {
    if (uuid) {
      loadCampaign();
    }
  }, [uuid]);

  const loadCampaign = async () => {
    if (!uuid) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getPublicCampaign(uuid);
      if (data.type !== "form") {
        setError(t("campaign.notQuestionnaire"));
        return;
      }
      setCampaign(data);
    } catch (err: unknown) {
      console.error("Failed to load campaign:", err);
      // JUB-188: never surface the backend message here, it is English only.
      setError(t("campaign.notFound"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!uuid || !campaign) return;

    // Validate required fields
    for (const question of campaign.questions) {
      if (question.required && !responses[question.id]) {
        toast.error(t("campaign.answerQuestion", { question: question.text }));
        // In step mode, go to that question
        if (campaign.displayMode === "step") {
          const stepIndex = campaign.questions.findIndex((q) => q.id === question.id);
          setCurrentStep(stepIndex);
        }
        return;
      }
    }

    try {
      setSubmitting(true);
      const responseList: QuestionnaireResponse[] = campaign.questions.map((q) => ({
        questionId: q.id,
        value: responses[q.id] || null,
      }));

      const result = await submitQuestionnaire(uuid, responseList);

      setVoucherCode(result.code);
      setThankYouMessage(result.thankYouMessage);
      setCtaUrl(result.ctaUrl);
      setCtaText(result.ctaText);
      setSubmitted(true);

      // If user is not authenticated, store in localStorage
      if (!result.assigned) {
        storePendingVoucher(uuid, result.code);
      }

      toast.success(t("campaign.questionnaireThanks"));
    } catch (err: unknown) {
      console.error("Failed to submit questionnaire:", err);
      const errorMessage = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast.error(errorMessage || t("campaign.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const updateResponse = (questionId: number, value: string) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const updateMultipleChoice = (questionId: number, option: string, checked: boolean) => {
    setResponses((prev) => {
      const currentValue = prev[questionId] || "[]";
      let selected: string[] = [];
      try {
        selected = JSON.parse(currentValue);
      } catch {
        selected = [];
      }

      if (checked) {
        selected = [...selected, option];
      } else {
        selected = selected.filter((o) => o !== option);
      }

      return { ...prev, [questionId]: JSON.stringify(selected) };
    });
  };

  const isMultipleChoiceSelected = (questionId: number, option: string): boolean => {
    const currentValue = responses[questionId] || "[]";
    try {
      const selected = JSON.parse(currentValue);
      return selected.includes(option);
    } catch {
      return false;
    }
  };

  const copyToClipboard = () => {
    if (voucherCode) {
      navigator.clipboard.writeText(voucherCode);
      toast.success(t("common.copied"));
    }
  };

  const renderQuestion = (question: PublicCampaignQuestion) => {
    return (
      <div key={question.id} className="space-y-3" data-testid={`campaign-question-${question.id}`}>
        <Label className="text-base">
          {question.text}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </Label>

        {question.type === "text" && (
          <Input
            value={responses[question.id] || ""}
            onChange={(e) => updateResponse(question.id, e.target.value)}
            placeholder={t("campaign.answerPlaceholder")}
            data-testid={`campaign-question-input-${question.id}`}
          />
        )}

        {question.type === "single_choice" && question.options && (
          <RadioGroup
            value={responses[question.id] || ""}
            onValueChange={(value) => updateResponse(question.id, value)}
          >
            {question.options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`q${question.id}-opt${index}`} />
                <Label htmlFor={`q${question.id}-opt${index}`} className="font-normal cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}

        {question.type === "multiple_choice" && question.options && (
          <div className="space-y-2">
            {question.options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Checkbox
                  id={`q${question.id}-opt${index}`}
                  checked={isMultipleChoiceSelected(question.id, option)}
                  onCheckedChange={(checked) =>
                    updateMultipleChoice(question.id, option, checked === true)
                  }
                />
                <Label htmlFor={`q${question.id}-opt${index}`} className="font-normal cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <PublicContentLayout>
        <div className="py-16 text-center space-y-6">
          <Card className="max-w-md w-full mx-auto">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground" data-testid="campaign-error">{error}</p>
            </CardContent>
          </Card>
          <Link to={PUBLIC_ROUTES.home(locale)}>
            <Button variant="outline">{t("httpErrors.404.backHome")}</Button>
          </Link>
        </div>
      </PublicContentLayout>
    );
  }

  if (!campaign) {
    return null;
  }

  // Success state
  if (submitted && voucherCode) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="campaign-success">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">{t("campaign.thankYouTitle")}</CardTitle>
            {thankYouMessage && (
              <CardDescription className="text-base" data-testid="campaign-thank-you">{thankYouMessage}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">{t("campaign.voucherCodeLabel")}</p>
              <div className="flex items-center justify-center gap-2">
                <code className="text-2xl font-bold tracking-wider" data-testid="campaign-voucher-code">{voucherCode}</code>
                <Button variant="ghost" size="sm" onClick={copyToClipboard}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {!isAuthenticated && (
              <p className="text-sm text-amber-600 text-center">
                {t("campaign.loginToSave")}
              </p>
            )}

            {ctaUrl && ctaText && (
              <Button className="w-full" onClick={() => window.location.href = ctaUrl}>
                {ctaText}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step-by-step mode
  if (campaign.displayMode === "step") {
    const currentQuestion = campaign.questions[currentStep];
    const isLastStep = currentStep === campaign.questions.length - 1;
    const isFirstStep = currentStep === 0;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Gift className="h-5 w-5 text-pink-500" />
              <span className="text-sm text-muted-foreground">
                {campaign.name}
              </span>
            </div>
            <CardTitle>{t("campaign.questionProgress", { current: currentStep + 1, total: campaign.questions.length })}</CardTitle>
            <div className="w-full bg-muted rounded-full h-2 mt-2">
              <div
                className="bg-pink-500 h-2 rounded-full transition-all"
                style={{ width: `${((currentStep + 1) / campaign.questions.length) * 100}%` }}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderQuestion(currentQuestion)}

            <div className="flex gap-3">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep((s) => s - 1)}
                  className="flex-1"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t("campaign.back")}
                </Button>
              )}
              {!isLastStep ? (
                <Button
                  onClick={() => {
                    if (currentQuestion.required && !responses[currentQuestion.id]) {
                      toast.error(t("campaign.answerThisQuestion"));
                      return;
                    }
                    setCurrentStep((s) => s + 1);
                  }}
                  className="flex-1"
                >
                  {t("campaign.next")}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1"
                  data-testid="campaign-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("campaign.submitting")}
                    </>
                  ) : (
                    <>
                      {t("campaign.submit")}
                      <CheckCircle2 className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // All-at-once mode
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Gift className="h-5 w-5 text-pink-500" />
            <span className="text-sm text-muted-foreground">
              {t("campaign.specialOffer")}
            </span>
          </div>
          <CardTitle className="text-2xl">{campaign.name}</CardTitle>
          <CardDescription>
            {t("campaign.questionnaireIntro")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {campaign.questions.map((question) => renderQuestion(question))}

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full"
            data-testid="campaign-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("campaign.submitting")}
              </>
            ) : (
              <>
                {t("campaign.getDiscountCode")}
                <Gift className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
